import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/conflict';
import { qk } from '@/lib/queryKeys';
import { DataState } from '@/components/common/DataState';
import { formatMoney } from '@/lib/money';

// --- Types ---

interface TeamOption {
  id: string;
  name: string;
  slug: string;
  plan: string;
  ownerName?: string;
  ownerEmail?: string;
  projectName?: string;
  projectSlug?: string;
}

// Single source of truth for how a team is labeled in every dropdown on this
// page. Several teams can share the same name + owner + plan, so the label
// leads with the recognizable project/site name (when the team has one) and
// always ends with the unique slug — guaranteeing no two options ever read
// identically.
function formatTeamOption(t: TeamOption): string {
  // Prefer the owner's email — it's unique per user, whereas display names
  // routinely collide (two "Shikha Gupta"s). Fall back to name, then to nothing.
  const who = t.ownerEmail || t.ownerName || 'no owner';
  const site = t.projectName || t.projectSlug;
  const head = site ? `${t.name} · ${site}` : t.name;
  return `${head} — ${who} · ${t.plan} · ${t.slug}`;
}

interface OfflinePayment {
  id: string;
  teamId: string;
  amount: number;
  currency: string;
  plan: string;
  reference: string;
  notes?: string;
  createdAt: string;
}

type VoucherType = 'credits' | 'percentage' | 'fixed';

interface Voucher {
  id: string;
  code: string;
  type: VoucherType;
  value: number;
  maxRedemptions?: number;
  currentRedemptions: number;
  status: string;
  createdAt: string;
}

interface ProductOption {
  ref: { type: string; id: string };
  name: string;
  // Real payable tiers for plugin/add-on products (tier id + minor-unit
  // price), from /admin/payment-links/products. Empty for platform.
  tiers?: Array<{ tier: string; displayName: string; price: number; currency: string }>;
}

// Per-team subscription-editor metadata (currency + payable product tiers).
// TODO: promote to qk
const subscriptionEditorMetaKey = (teamId: string) =>
  ['admin', 'subscription-editor-meta', teamId] as const;

/**
 * Shared teams dropdown. Every tab reads the same `qk.adminTeams()` key, so
 * React Query dedupes the fetch — switching tabs reuses the cached list
 * instead of re-hitting /admin/billing/teams once per tab.
 */
function useAdminTeams() {
  return useQuery({
    queryKey: qk.adminTeams(),
    queryFn: async () => {
      const res = await api.get('/admin/billing/teams');
      return (res.data.data ?? []) as TeamOption[];
    },
  });
}

export default function AdminBillingPage() {
  const [activeTab, setActiveTab] = useState<'offline' | 'vouchers' | 'credits' | 'subscription'>(
    'offline'
  );

  const tabs: Array<{ key: typeof activeTab; label: string }> = [
    { key: 'offline', label: 'Offline Payments' },
    { key: 'vouchers', label: 'Vouchers' },
    { key: 'credits', label: 'Credit Adjustment' },
    { key: 'subscription', label: 'Subscription Editor' },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Admin Billing</h1>
        <p className="text-gray-500">
          Manage offline payments, vouchers, credit balances, and subscription records.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 rounded-lg border bg-gray-50 p-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`rounded-md px-4 py-2 text-sm font-medium ${
              activeTab === t.key
                ? 'bg-white text-blue-600 shadow'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'offline' && <OfflinePaymentsTab />}
      {activeTab === 'vouchers' && <VouchersTab />}
      {activeTab === 'credits' && <CreditAdjustmentTab />}
      {activeTab === 'subscription' && <SubscriptionEditorTab />}
    </div>
  );
}

// =============================================================================
// Credit Adjustment Tab — superadmin direct grant/deduct
// =============================================================================

function CreditAdjustmentTab() {
  const teamsQuery = useAdminTeams();
  const teams = teamsQuery.data ?? [];
  const [form, setForm] = useState({
    teamId: '',
    delta: '',
    reason: '',
    allowNegative: false,
  });
  const [lastResult, setLastResult] = useState<{
    previousBalance: number;
    newBalance: number;
    delta: number;
  } | null>(null);

  useEffect(() => {
    if (teamsQuery.isError) toast.error('Failed to load teams');
  }, [teamsQuery.isError]);

  const adjustMutation = useMutation({
    mutationFn: async (payload: {
      teamId: string;
      delta: number;
      reason: string;
      allowNegative: boolean;
    }) => {
      const res = await api.post('/admin/billing/credits/adjust', payload);
      return res.data.data as { previousBalance: number; newBalance: number; delta: number };
    },
    onSuccess: (result, payload) => {
      setLastResult(result);
      toast.success(
        `${payload.delta > 0 ? 'Granted' : 'Deducted'} ${Math.abs(payload.delta)} credits. Balance: ${result.previousBalance} → ${result.newBalance}`
      );
      setForm((f) => ({ teamId: f.teamId, delta: '', reason: '', allowNegative: false }));
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Adjustment failed'));
    },
  });
  const busy = adjustMutation.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.teamId || !form.delta || !form.reason) {
      toast.error('Team, delta and reason are required.');
      return;
    }
    const deltaNum = Number(form.delta);
    if (!Number.isFinite(deltaNum) || deltaNum === 0) {
      toast.error('Delta must be a non-zero number.');
      return;
    }
    adjustMutation.mutate({
      teamId: form.teamId,
      delta: deltaNum,
      reason: form.reason,
      allowNegative: form.allowNegative,
    });
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold">Adjust Team Credit Balance</h2>
        <p className="text-muted-foreground mb-4 text-sm">
          Grant or deduct credits on any team. Use this after recording an offline payment (positive
          delta) or to correct a mistaken grant (negative delta). Every adjustment writes a
          CreditTransaction + audit log row. Credits are in the team's locked currency unit (₹1 = 1
          INR credit on an Indian team, $1 = 1 USD credit on a US team).
        </p>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm sm:col-span-2">
            Team
            <select
              value={form.teamId}
              onChange={(e) => setForm({ ...form, teamId: e.target.value })}
              className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
              disabled={busy}
            >
              <option value="">— Select a team —</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {formatTeamOption(t)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            Delta (positive = grant, negative = deduct)
            <input
              type="number"
              value={form.delta}
              onChange={(e) => setForm({ ...form, delta: e.target.value })}
              placeholder="e.g. 12000 or -500"
              className="mt-1 w-full rounded-lg border px-3 py-2"
              disabled={busy}
            />
          </label>
          <label className="block text-sm">
            Reason (recorded in audit log)
            <input
              type="text"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Offline INR 12000 from bank transfer ref XYZ"
              className="mt-1 w-full rounded-lg border px-3 py-2"
              disabled={busy}
              maxLength={500}
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={form.allowNegative}
              onChange={(e) => setForm({ ...form, allowNegative: e.target.checked })}
              disabled={busy}
            />
            Allow balance to go negative (use only for chargebacks / refund corrections)
          </label>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={busy || !form.teamId || !form.delta || !form.reason}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? 'Applying…' : 'Apply adjustment'}
            </button>
          </div>
        </form>
      </div>

      {lastResult && (
        <div className="rounded-xl border bg-green-50 p-4 text-sm">
          Last adjustment: balance went{' '}
          <strong>
            {lastResult.previousBalance} → {lastResult.newBalance}
          </strong>{' '}
          (delta {lastResult.delta > 0 ? '+' : ''}
          {lastResult.delta}).
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Subscription Editor Tab — fix existing periods / plan / status / gateway
// =============================================================================

function SubscriptionEditorTab() {
  const queryClient = useQueryClient();
  const teamsQuery = useAdminTeams();
  const teams = teamsQuery.data ?? [];
  const [teamId, setTeamId] = useState('');
  const [form, setForm] = useState({
    plan: '',
    status: '',
    gateway: '',
    gatewaySubscriptionId: '',
    currentPeriodStart: '',
    currentPeriodEnd: '',
    cancelAtPeriodEnd: false,
    paymentMethod: '',
  });
  // Generic product selector: 'platform:tyashin' or 'plugin:<id>' / 'addon:<id>'
  const [product, setProduct] = useState('platform:tyashin');

  useEffect(() => {
    if (teamsQuery.isError) toast.error('Failed to load teams');
  }, [teamsQuery.isError]);

  // Per-team editor metadata. The customer-facing /billing/subscription is
  // teamAuth-scoped; for SUPER_ADMIN we just hit /billing/pricing?teamId= to
  // grab the currency, and let the user enter the dates / status by hand.
  // Avoids needing a new "get sub" admin route. payment-links/products = same
  // product list PLUS real payable tiers per product, so the tier dropdown
  // below can offer only tiers that exist.
  const metaQuery = useQuery({
    queryKey: subscriptionEditorMetaKey(teamId),
    queryFn: async () => {
      const [pricing, prods] = await Promise.all([
        api.get(`/billing/pricing?teamId=${encodeURIComponent(teamId)}`).catch(() => null),
        api
          .get(`/admin/payment-links/products?teamId=${encodeURIComponent(teamId)}`)
          .catch(() => null),
      ]);
      return {
        currency: ((pricing as any)?.data?.data?.currency as string | undefined) ?? '',
        products: ((prods as any)?.data?.data?.products ?? []) as ProductOption[],
      };
    },
    enabled: Boolean(teamId),
  });
  const currency = teamId ? (metaQuery.data?.currency ?? '') : '';
  const products = metaQuery.data?.products ?? [];
  const loadingSub = Boolean(teamId) && metaQuery.isPending;

  // Team switch is UI state, not server data: reset the product selector and
  // pre-fill the plan from the teams list. The rest of the form intentionally
  // carries over (matches previous behavior — only plan pre-fills per team).
  const handleTeamChange = (nextTeamId: string) => {
    setTeamId(nextTeamId);
    if (!nextTeamId) {
      setForm({
        plan: '',
        status: '',
        gateway: '',
        gatewaySubscriptionId: '',
        currentPeriodStart: '',
        currentPeriodEnd: '',
        cancelAtPeriodEnd: false,
        paymentMethod: '',
      });
      return;
    }
    setProduct('platform:tyashin');
    const t = teams.find((x) => x.id === nextTeamId);
    if (t) setForm((f) => ({ ...f, plan: t.plan }));
  };

  const saveMutation = useMutation({
    mutationFn: async (input: { kind: 'platform' | 'addon'; body: Record<string, any> }) => {
      if (input.kind === 'platform') {
        await api.patch(`/admin/billing/teams/${teamId}/subscription`, input.body);
        return { promo: undefined as { emailedTo?: string } | undefined };
      }
      const res = await api.post(`/admin/billing/teams/${teamId}/addon-validity`, input.body);
      return { promo: (res as any)?.data?.data?.promo as { emailedTo?: string } | undefined };
    },
    onSuccess: ({ promo }) => {
      // Plan / status edits change what the team dropdown labels show.
      queryClient.invalidateQueries({ queryKey: qk.adminTeams() });
      if (promo) {
        toast.success(
          `Promo granted — ₹0 invoice created${promo.emailedTo ? `, owner emailed (${promo.emailedTo})` : ''}.`
        );
        return;
      }
      toast.success('Subscription updated.');
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Update failed'));
    },
  });
  const busy = saveMutation.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!teamId) {
      toast.error('Pick a team.');
      return;
    }
    const [ptype, pid] = product.split(':');
    if (ptype === 'platform') {
      const body: Record<string, any> = {};
      if (form.plan) body.plan = form.plan;
      if (form.status) body.status = form.status;
      if (form.gateway) body.gateway = form.gateway;
      if (form.gatewaySubscriptionId) {
        body.gatewaySubscriptionId = form.gatewaySubscriptionId.trim();
      }
      if (form.currentPeriodStart) {
        body.currentPeriodStart = new Date(form.currentPeriodStart).toISOString();
      }
      if (form.currentPeriodEnd) {
        body.currentPeriodEnd = new Date(form.currentPeriodEnd).toISOString();
      }
      if (form.cancelAtPeriodEnd) body.cancelAtPeriodEnd = true;
      if (Object.keys(body).length === 0) {
        toast.error('Nothing to change — set at least one field.');
        return;
      }
      saveMutation.mutate({ kind: 'platform', body });
      return;
    }
    // plugin / add-on: tier + status + period. The backend syncs
    // pluginConfigs.config.plan so the customer's plugin page and runtime
    // caps update immediately. (This branch used to silently DROP the
    // selected tier — green toast, no change — which is exactly how a
    // "upgraded to pro but still shows free" support ticket happens.)
    const body: Record<string, any> = { pluginId: pid };
    if (form.plan) body.tier = form.plan;
    if (form.status) body.status = form.status === 'cancelled' ? 'cancelled' : 'active';
    if (form.currentPeriodEnd) {
      body.currentPeriodEnd = new Date(form.currentPeriodEnd).toISOString();
    }
    if (form.paymentMethod) body.paymentMethod = form.paymentMethod;
    // Promo = "Tyashin pays": needs the tier being gifted and when it ends.
    if (form.paymentMethod === 'promo' && (!form.plan || form.plan === 'free')) {
      toast.error('A promo upgrade needs a paid tier — pick the tier you are gifting.');
      return;
    }
    if (form.paymentMethod === 'promo' && !form.currentPeriodEnd) {
      toast.error('A promo upgrade needs a period end — when the complimentary access expires.');
      return;
    }
    if (Object.keys(body).length === 1) {
      toast.error('Nothing to change — set a tier, status, period end, or payment method.');
      return;
    }
    saveMutation.mutate({ kind: 'addon', body });
  }

  return (
    <div className="rounded-xl border bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold">Edit Team Subscription</h2>
      <p className="text-muted-foreground mb-4 text-sm">
        Direct-edit a billing_subscriptions row. Use to correct period dates (e.g. a credit-pay flow
        that hardcoded 30 days when you meant calendar-month-from-1st), change the plan, or unblock
        a past_due team. Upserts — also creates a subscription doc if the team doesn't have one yet
        (legacy offline-paid teams). Every change is audit-logged.
      </p>

      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm sm:col-span-2">
          Team {currency && <span className="text-muted-foreground">(currency: {currency})</span>}
          <select
            value={teamId}
            onChange={(e) => handleTeamChange(e.target.value)}
            className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
            disabled={busy || loadingSub}
          >
            <option value="">— Select a team —</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {formatTeamOption(t)}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm sm:col-span-2">
          Product
          <select
            value={product}
            onChange={(e) => {
              setProduct(e.target.value);
              // Platform plans and plugin tiers are different id spaces —
              // never carry a selection (or a promo flag) across product kinds.
              setForm((f) => ({ ...f, plan: '', paymentMethod: '' }));
            }}
            className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
            disabled={busy || !teamId}
          >
            {(products.length
              ? products
              : [{ ref: { type: 'platform', id: 'tyashin' }, name: 'Tyashin Platform' }]
            ).map((p) => (
              <option key={`${p.ref.type}:${p.ref.id}`} value={`${p.ref.type}:${p.ref.id}`}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        {product.startsWith('platform') && (
          <label className="block text-sm sm:col-span-2">
            Gateway subscription ID{' '}
            <span className="text-muted-foreground">(link an existing sub, e.g. sub_xxx)</span>
            <input
              type="text"
              value={form.gatewaySubscriptionId}
              onChange={(e) => setForm({ ...form, gatewaySubscriptionId: e.target.value })}
              placeholder="sub_…"
              className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
              disabled={busy || !teamId}
            />
          </label>
        )}

        <label className="block text-sm">
          {product.startsWith('platform') ? 'Plan' : 'Tier'}
          <select
            value={form.plan}
            onChange={(e) => setForm({ ...form, plan: e.target.value })}
            className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
            disabled={busy || !teamId}
          >
            <option value="">— leave unchanged —</option>
            {product.startsWith('platform')
              ? ['free', 'starter', 'pro', 'business', 'enterprise'].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))
              : [
                  <option key="free" value="free">
                    free (downgrade)
                  </option>,
                  ...(
                    products.find((p) => `${p.ref.type}:${p.ref.id}` === product)?.tiers ?? []
                  ).map((t) => (
                    <option key={t.tier} value={t.tier}>
                      {/* tier prices are canonical minor units */}
                      {t.tier} — {t.displayName} ({formatMoney(t.price, t.currency)}/mo)
                    </option>
                  )),
                ]}
          </select>
        </label>

        {!product.startsWith('platform') && (
          <label className="block text-sm">
            Payment method
            <select
              value={form.paymentMethod}
              onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
              className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
              disabled={busy || !teamId}
            >
              <option value="">— leave unchanged —</option>
              <option value="offline">offline — recorded manual payment</option>
              <option value="promo">promo — free upgrade, sponsored by Tyashin</option>
            </select>
            {form.paymentMethod === 'promo' && (
              <span className="text-muted-foreground mt-1 block text-xs">
                Gifts the selected tier until the period end below. The customer gets a ₹0
                &ldquo;Sponsored by Tyashin&rdquo; invoice in Payments &amp; Invoices and the team
                owner is emailed. At the end date the plugin drops back to free automatically —
                nothing is ever charged.
              </span>
            )}
          </label>
        )}

        <label className="block text-sm">
          Status
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
            disabled={busy || !teamId}
          >
            <option value="">— leave unchanged —</option>
            {['active', 'trialing', 'past_due', 'cancelled', 'incomplete'].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          Gateway
          <select
            value={form.gateway}
            onChange={(e) => setForm({ ...form, gateway: e.target.value })}
            className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
            disabled={busy || !teamId}
          >
            <option value="">— leave unchanged —</option>
            {['credits', 'stripe', 'razorpay', 'offline'].map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          Current period start
          <input
            type="datetime-local"
            value={form.currentPeriodStart}
            onChange={(e) => setForm({ ...form, currentPeriodStart: e.target.value })}
            className="mt-1 w-full rounded-lg border px-3 py-2"
            disabled={busy || !teamId}
          />
        </label>

        <label className="block text-sm">
          Current period end (= next renewal)
          <input
            type="datetime-local"
            value={form.currentPeriodEnd}
            onChange={(e) => setForm({ ...form, currentPeriodEnd: e.target.value })}
            className="mt-1 w-full rounded-lg border px-3 py-2"
            disabled={busy || !teamId}
          />
        </label>

        <label className="inline-flex items-center gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            checked={form.cancelAtPeriodEnd}
            onChange={(e) => setForm({ ...form, cancelAtPeriodEnd: e.target.checked })}
            disabled={busy || !teamId}
          />
          Cancel at period end (renewal cron will not re-deduct)
        </label>

        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={busy || !teamId}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save subscription edits'}
          </button>
        </div>
      </form>
    </div>
  );
}

// =============================================================================
// Offline Payments Tab
// =============================================================================

function OfflinePaymentsTab() {
  const queryClient = useQueryClient();
  // Teams failure is non-fatal — the team dropdown will just be empty.
  const teamsQuery = useAdminTeams();
  const teams = teamsQuery.data ?? [];

  const paymentsQuery = useQuery({
    queryKey: qk.offlinePayments(),
    queryFn: async () => {
      const res = await api.get('/admin/billing/offline-payments');
      return (res.data.data ?? []) as OfflinePayment[];
    },
  });

  const [form, setForm] = useState({
    teamId: '',
    amount: '',
    currency: 'USD',
    plan: 'pro',
    reference: '',
    notes: '',
  });

  // Resolve team name for display in list
  function teamLabel(teamId: string) {
    const t = teams.find((t) => t.id === teamId);
    if (!t) return teamId;
    const site = t.projectName || t.projectSlug;
    return site ? `${t.name} · ${site} (${t.slug})` : `${t.name} (${t.slug})`;
  }

  const logMutation = useMutation({
    mutationFn: (payload: {
      teamId: string;
      amount: number;
      currency: string;
      plan: string;
      reference: string;
      notes?: string;
    }) => api.post('/admin/billing/offline-payments', payload),
    onSuccess: () => {
      toast.success('Offline payment logged');
      setForm({ teamId: '', amount: '', currency: 'USD', plan: 'pro', reference: '', notes: '' });
      queryClient.invalidateQueries({ queryKey: qk.offlinePayments() });
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to log payment'));
    },
  });
  const submitting = logMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.teamId || !form.amount || !form.reference) return;

    logMutation.mutate({
      teamId: form.teamId,
      amount: Number(form.amount),
      currency: form.currency,
      plan: form.plan,
      reference: form.reference,
      notes: form.notes || undefined,
    });
  }

  return (
    <div className="space-y-6">
      {/* Form */}
      <div className="rounded-lg border p-6">
        <h2 className="mb-4 text-lg font-semibold">Log Offline Payment</h2>
        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium">Team</label>
            <select
              value={form.teamId}
              onChange={(e) => setForm({ ...form, teamId: e.target.value })}
              className="w-full rounded-md border px-3 py-2"
              required
            >
              <option value="">Select a team…</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {formatTeamOption(t)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Amount (cents)</label>
            <input
              type="number"
              placeholder="e.g. 9900"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full rounded-md border px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Currency</label>
            <select
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="w-full rounded-md border px-3 py-2"
            >
              <option value="USD">USD</option>
              <option value="INR">INR</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Plan</label>
            <select
              value={form.plan}
              onChange={(e) => setForm({ ...form, plan: e.target.value })}
              className="w-full rounded-md border px-3 py-2"
            >
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="business">Business</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Reference</label>
            <input
              type="text"
              placeholder="Payment reference / receipt #"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className="w-full rounded-md border px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Notes (optional)</label>
            <input
              type="text"
              placeholder="Optional notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full rounded-md border px-3 py-2"
            />
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Logging...' : 'Log Payment'}
            </button>
          </div>
        </form>
      </div>

      {/* List */}
      <div className="rounded-lg border">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Recent Offline Payments</h2>
        </div>
        <DataState
          query={paymentsQuery}
          skeleton={
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
            </div>
          }
        >
          {(payments) =>
            payments.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-400">
                No offline payments recorded.
              </div>
            ) : (
              <div className="divide-y">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between px-6 py-4 text-sm">
                    <div>
                      <p className="font-medium">
                        {formatMoney(p.amount, p.currency, { alwaysDecimals: true })} &mdash;{' '}
                        <span className="capitalize">{p.plan}</span>
                      </p>
                      <p className="text-gray-500">
                        Team: {teamLabel(p.teamId)} &middot; Ref: {p.reference}
                      </p>
                      {p.notes && <p className="text-gray-400">{p.notes}</p>}
                    </div>
                    <span className="text-gray-400">
                      {new Date(p.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )
          }
        </DataState>
      </div>
    </div>
  );
}

// =============================================================================
// Vouchers Tab
// =============================================================================

function VouchersTab() {
  const queryClient = useQueryClient();

  const vouchersQuery = useQuery({
    queryKey: qk.vouchers(),
    queryFn: async () => {
      const res = await api.get('/admin/billing/vouchers');
      return (res.data.data ?? []) as Voucher[];
    },
  });

  const [form, setForm] = useState({
    code: '',
    type: 'credits' as VoucherType,
    value: '',
  });

  const createMutation = useMutation({
    mutationFn: (payload: { code: string; type: VoucherType; value: number }) =>
      api.post('/admin/billing/vouchers', payload),
    onSuccess: () => {
      toast.success('Voucher created');
      setForm({ code: '', type: 'credits', value: '' });
      queryClient.invalidateQueries({ queryKey: qk.vouchers() });
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to create voucher'));
    },
  });
  const submitting = createMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.code || !form.value) return;

    createMutation.mutate({
      code: form.code,
      type: form.type,
      value: Number(form.value),
    });
  }

  function voucherTypeLabel(type: string) {
    switch (type) {
      case 'credits':
        return 'Credits';
      case 'percentage':
        return 'Discount (%)';
      case 'fixed':
        return 'Discount (Fixed)';
      default:
        return type;
    }
  }

  function voucherValueDisplay(v: Voucher) {
    switch (v.type) {
      case 'credits':
        return `${v.value} credits`;
      case 'percentage':
        return `${v.value}% off`;
      case 'fixed':
        return `${v.value} off (fixed)`;
      default:
        return `${v.value}`;
    }
  }

  return (
    <div className="space-y-6">
      {/* Form */}
      <div className="rounded-lg border p-6">
        <h2 className="mb-4 text-lg font-semibold">Create Voucher</h2>
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Code</label>
            <input
              type="text"
              placeholder="e.g. WELCOME50"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              className="rounded-md border px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as VoucherType })}
              className="rounded-md border px-3 py-2"
            >
              <option value="credits">Credits</option>
              <option value="percentage">Discount (%)</option>
              <option value="fixed">Discount (Fixed)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Value</label>
            <input
              type="number"
              placeholder="Value"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              className="w-28 rounded-md border px-3 py-2"
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Creating...' : 'Create'}
          </button>
        </form>
      </div>

      {/* List */}
      <div className="rounded-lg border">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Existing Vouchers</h2>
        </div>
        <DataState
          query={vouchersQuery}
          skeleton={
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
            </div>
          }
        >
          {(vouchers) =>
            vouchers.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-400">No vouchers created yet.</div>
            ) : (
              <div className="divide-y">
                {vouchers.map((v) => (
                  <div key={v.id} className="flex items-center justify-between px-6 py-4 text-sm">
                    <div>
                      <p className="font-mono font-medium">{v.code}</p>
                      <p className="text-gray-500">
                        {voucherTypeLabel(v.type)} &middot; {voucherValueDisplay(v)} &middot; Used:{' '}
                        {v.currentRedemptions}
                        {v.maxRedemptions ? `/${v.maxRedemptions}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                          v.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {v.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-gray-400">
                        {new Date(v.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </DataState>
      </div>
    </div>
  );
}
